import React, { useCallback } from "react";
import _, { get } from "lodash";
import PropTypes from "prop-types";
import { dollars } from "LIB/formatters/currency";
import {
  CONDITION,
  CONSIGNMENT_TYPE,
  INVENTORIES,
  WARRANTY_OPTIONS
} from "MODULES/inventories/constants";
import {
  Check,
  CheckGroup,
  CurrencyField,
  FormInput,
  FormRow,
  NumberInput,
  Select,
  TextArea
} from "MODULES/shared/components/Forms";
import { Col, Collapse } from "react-bootstrap";
import { Field, Fields, FormSection } from "redux-form";
import { arrayToOptions, toOptions } from "LIB/forms";
import {
  RETURN_TO_FOB,
  RETURN_TO_LOCATION_ID,
  RETURN_TO_TYPE
} from "APP_ROOT/constants";
import { EMA } from "MODULES/Receiving/constants";
import {
  CONTINGENT_ON_PASSING,
  CUSTOMER_QC_PREAPPROVAL_REQUIRED,
  DOLLARS,
  EXCHANGE_SALE,
  EXCHANGE_SPECIAL_INSTRUCTIONS,
  FLAT_EXCHANGE,
  PERCENTAGE,
  REPAIR_ADMIN_CHARGE_TYPE,
  REPAIR_ADMINISTRATION_CHARGE_DOLLARS,
  REPAIR_ADMINISTRATION_CHARGE_PERCENTAGE,
  REPAIR_FEE,
  SELLER_ID
} from "MODULES/SalesOrders/constants";
import { properCase } from "LIB/formatters/text";
import { WARRANTY } from "MODULES/OutgoingQuotes/constants";
import { TENANT_LOCATION } from "MODULES/shared/components/ShippingSection/shippingTypes";
import ExistingQuoteWarning from "MODULES/SalesOrders/components/ConvertFromCartForm/ExistingQuoteWarning";
import { fobFormat } from "LIB/formatters/address";
import { DO_NOT_EXCHANGE } from "MODULES/companies/constants";
import RepairAdminCharge from "MODULES/SalesOrders/components/Detail/Body/LineItems/Form/PartDetails/RepairAdminCharge";
import { warrantyForCondition } from "../../../shared/helpers";
import PartFee from "./PartFee";
import { BlanketExchangeAgreementWarning } from "./Warnings";

export default function LineItemFields({
  canExchange,
  change,
  fields,
  getField,
  lineItems,
  disableCustomerCqcApproval,
  selectedCustomer,
  selectedSeller,
  shipToAddress
}) {
  const changeExchange = useCallback(
    (_ev, val) => {
      if (val) {
        change(RETURN_TO_TYPE, TENANT_LOCATION);
        change(RETURN_TO_LOCATION_ID, lineItems[0].store_location_id);
        if (shipToAddress) change(RETURN_TO_FOB, fobFormat(shipToAddress));
      } else {
        change("line_items[0].flat_exchange", val);
      }
    },
    [change, lineItems, shipToAddress]
  );

  return (
    <tbody>
      {fields.map((item, idx) => {
        const lineItem = lineItems[idx];
        const hazmat = fields.get(idx).hazmat;
        const specialContainer = fields.get(idx).special_container_req;
        const exchange = fields.get(idx).exchange_sale;
        const altOptions = lineItem.alternates.map(a => ({
          value: a.id,
          text: a.name
        }));
        const price =
          (exchange
            ? fields.get(idx).outright_value
            : fields.get(idx).price_per_unit) || 0;
        const subtotal = dollars(parseFloat(price) * lineItem.total_quantity);

        /* eslint-disable no-unused-vars */
        const {
          scrap,
          parts_unit,
          ...filteredConditions
        } = INVENTORIES.condition;
        /* eslint-enable no-unused-vars */

        const chargeType = getField(`${item}.repair_admin_charge_type`);

        return (
          <tr key={`item-${lineItem.id}`}>
            <td style={{ verticalAlign: "top", paddingTop: "12px" }}>
              {idx + 1}
            </td>
            <td>
              <table className="table">
                <tbody>
                  <ExistingQuoteWarning
                    part={lineItem.part}
                    company={selectedCustomer}
                    sellerId={getField(`${item}.${SELLER_ID}`)}
                  />
                  <tr>
                    <td>
                      <span className="float-left">
                        {lineItem.part.part_number} | serial #&nbsp;
                        {lineItem.serial_number} | in&nbsp;
                        {INVENTORIES.condition[lineItem.condition].label}{" "}
                        Condition | SRP {dollars(lineItem.suggested_retail)}
                        {lineItem.exchange_srp &&
                          ` | EX SRP ${dollars(lineItem.exchange_srp)}`}
                      </span>
                      <span className="float-right">{subtotal}</span>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <FormRow>
                        <Col md={12}>
                          <Collapse
                            in={parseInt(lineItem.total_quantity, 10) === 1}
                            unmountOnExit
                          >
                            <div>
                              <FormRow>
                                <Col md={3}>
                                  <CheckGroup title="Exchange Sale" justified>
                                    <Field
                                      name={`${item}.exchange_sale`}
                                      component={Check}
                                      label={properCase(EXCHANGE_SALE)}
                                      disabled={
                                        !canExchange ||
                                        getField(
                                          `${item}.${CONSIGNMENT_TYPE}`
                                        ) === EMA ||
                                        lineItem.brokered_exchange ||
                                        _.get(
                                          selectedCustomer,
                                          DO_NOT_EXCHANGE
                                        ) ||
                                        lineItem.do_not_exchange
                                      }
                                      onChange={(e, val) =>
                                        changeExchange(e, val)}
                                    />
                                  </CheckGroup>
                                </Col>
                                {exchange && (
                                  <React.Fragment>
                                    <Col md={3}>
                                      <CheckGroup
                                        title={properCase(FLAT_EXCHANGE)}
                                        justified
                                      >
                                        <Field
                                          name={`${item}.${FLAT_EXCHANGE}`}
                                          component={Check}
                                          label={properCase(FLAT_EXCHANGE)}
                                          disabled={
                                            !!_.get(
                                              lineItem,
                                              "purchase_order_line_item.flat_exchange"
                                            )
                                          }
                                        />
                                      </CheckGroup>
                                    </Col>
                                    <FormSection name={item}>
                                      <Col md={4}>
                                        <Fields
                                          names={[
                                            REPAIR_ADMIN_CHARGE_TYPE,
                                            REPAIR_ADMINISTRATION_CHARGE_PERCENTAGE,
                                            REPAIR_ADMINISTRATION_CHARGE_DOLLARS
                                          ]}
                                          component={RepairAdminCharge}
                                        />
                                      </Col>
                                      <Col md={2}>
                                        {chargeType === PERCENTAGE && (
                                          <Field
                                            name={
                                              REPAIR_ADMINISTRATION_CHARGE_PERCENTAGE
                                            }
                                            component={FormInput}
                                            label="Repair Charge (%)"
                                          />
                                        )}
                                        {chargeType === DOLLARS && (
                                          <Field
                                            name={
                                              REPAIR_ADMINISTRATION_CHARGE_DOLLARS
                                            }
                                            component={FormInput}
                                            label="Repair Charge ($)"
                                          />
                                        )}
                                      </Col>
                                    </FormSection>
                                  </React.Fragment>
                                )}
                                <Col md={7}>
                                  <BlanketExchangeAgreementWarning
                                    company={selectedCustomer}
                                    exchangeSale={exchange}
                                  />
                                </Col>
                              </FormRow>
                              <Collapse in={exchange} unmountOnExit>
                                <div>
                                  <FormRow>
                                    <Col md={3}>
                                      <CurrencyField
                                        name={`${item}.exchange_fee`}
                                        label={
                                          getField(`${item}.${FLAT_EXCHANGE}`)
                                            ? "Flat Exchange Fee"
                                            : "Exchange Fee"
                                        }
                                        required
                                      />
                                    </Col>
                                    <Col md={3}>
                                      <CurrencyField
                                        name={`${item}.outright_value`}
                                        label="Outright Value"
                                        required
                                      />
                                    </Col>
                                    <Col md={2}>
                                      <Field
                                        name={`${item}.core_due_days`}
                                        label="Core Due Days"
                                        component={NumberInput}
                                        roundDigits={0}
                                      />
                                    </Col>
                                    <Col md={3}>
                                      <Field
                                        name={`${item}.${REPAIR_FEE}`}
                                        label="Avg Repair (NTX)"
                                        component={FormInput}
                                        required={getField(
                                          `${item}.${FLAT_EXCHANGE}`
                                        )}
                                      />
                                    </Col>
                                  </FormRow>
                                  <FormRow>
                                    <Col md={12}>
                                      <Field
                                        name={`${item}.${EXCHANGE_SPECIAL_INSTRUCTIONS}`}
                                        component={TextArea}
                                        maxLength={150}
                                        label="Special Instructions (To be incl. on Exch. Agreement)"
                                        rows="5"
                                      />
                                    </Col>
                                  </FormRow>
                                </div>
                              </Collapse>
                            </div>
                          </Collapse>
                        </Col>
                      </FormRow>
                      <FormRow>
                        {!exchange && (
                          <Col md={3}>
                            <CurrencyField
                              name={`${item}.price_per_unit`}
                              label="Sales Ea."
                              required
                            />
                          </Col>
                        )}
                        <Col md={3}>
                          <Field
                            label="Qty"
                            name={`${item}.read_only_quantity`}
                            component={FormInput}
                            inputProps={{ value: lineItem.total_quantity }}
                            readOnly
                            disabled
                          />
                        </Col>
                        <Col md={3}>
                          <Field
                            label={properCase(CONDITION)}
                            name={`${item}.condition`}
                            component={Select}
                            options={toOptions(filteredConditions)}
                            onChange={e =>
                              change(
                                `${item}.warranty`,
                                warrantyForCondition(
                                  e.target.value,
                                  lineItem.part,
                                  lineItem.tag_date
                                )
                              )}
                          />
                        </Col>
                        <Col md={3}>
                          <Field
                            name={`${item}.sell_as_alt_id`}
                            component={Select}
                            label="Sell As Alternate"
                            options={altOptions}
                          />
                        </Col>
                      </FormRow>
                    </td>
                  </tr>
                  <PartFee
                    currentSeller={selectedSeller}
                    item={item}
                    part={lineItem.part}
                    hazmat={hazmat}
                    specialContainer={specialContainer}
                    change={change}
                    getField={getField}
                    changeExchange={changeExchange}
                    canExchange={canExchange}
                    lineItem={lineItem}
                    selectedCustomer={selectedCustomer}
                    left={
                      <Col md={3}>
                        <Field
                          label="Days Lead Time"
                          name={`${item}.lead_time`}
                          component={FormInput}
                        />
                      </Col>
                    }
                  />
                  <tr>
                    <td>
                      <div>
                        <h5 className="h4">QC Pre-Approval Required</h5>
                        <FormRow>
                          <Col md={5}>
                            <CheckGroup
                              title="QC Pre-Approval Required"
                              justified
                            >
                              <Field
                                name={`${item}.${CUSTOMER_QC_PREAPPROVAL_REQUIRED}`}
                                component={Check}
                                label="Required"
                                disabled={disableCustomerCqcApproval(
                                  selectedCustomer,
                                  lineItem
                                )}
                              />
                            </CheckGroup>
                          </Col>
                          {get(lineItem, CONTINGENT_ON_PASSING) && (
                            <Col md={3}>
                              <CheckGroup
                                title="Contingent To Passing"
                                justified
                              >
                                <Field
                                  name={`${item}.${CONTINGENT_ON_PASSING}`}
                                  component={Check}
                                  label="Contingent To Passing"
                                />
                              </CheckGroup>
                            </Col>
                          )}
                        </FormRow>
                      </div>
                      <div>
                        <h5 className="h4">Warranty</h5>
                        <FormRow>
                          <Col md={3}>
                            <Field
                              component={Select}
                              label={properCase(WARRANTY)}
                              name={`${item}.warranty`}
                              options={arrayToOptions(WARRANTY_OPTIONS)}
                            />
                          </Col>
                        </FormRow>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        );
      })}
    </tbody>
  );
}

LineItemFields.propTypes = {
  canExchange: PropTypes.bool,
  change: PropTypes.func.isRequired,
  disableCustomerCqcApproval: PropTypes.func.isRequired,
  fields: PropTypes.shape({
    map: PropTypes.func.isRequired,
    get: PropTypes.func.isRequired
  }).isRequired,
  getField: PropTypes.func.isRequired,
  lineItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedCustomer: PropTypes.shape({}),
  selectedSeller: PropTypes.shape({}),
  shipToAddress: PropTypes.shape({})
};



// WEBPACK FOOTER //
// ./app/modules/SalesOrders/components/ConvertFromCartForm/LineItemFields.jsx